/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 16/03/22
 */

import { LoadingSpinner } from '@/components';
import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import FocusHeader from '@/components/FocusHeader/FocusHeader';
import { UserAccessCustomisation } from '@/components/pages/Admin/UserAccessCustomisation';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import usePageTitle from '@/utils/hooks/usePageTitle';
import { useQuery } from '@/utils/hooks/useQuery';
import { Box, Flex, Tab, TabList, TabPanel, TabPanels, Tabs, VStack } from '@chakra-ui/react';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import AdminUserSupervision from '@/components/pages/Admin/AdminUserSupervision';
import { useSearchParams } from 'react-router-dom';
import AdminAuditLog from '@/components/pages/Admin/AdminAuditLog';

const Admin = () => {
    usePageTitle('Administrator');
    const { currentAssignment, user, loading, error } = useAssignment();
    const { isEitherAdmin, isGlobalAdmin } = useMemo(() => {
        const isGlobalAdmin = !!currentAssignment?.isGlobalAdmin;
        const isFacultyAdmin = !!currentAssignment?.adminFaculties?.length;
        const isOrganisationAdmin = !!currentAssignment?.adminOrganisations?.length;

        return {
            isGlobalAdmin,
            isFacultyAdmin,
            isOrganisationAdmin,
            isEitherAdmin: isGlobalAdmin || isFacultyAdmin || isOrganisationAdmin,
        };
    }, [currentAssignment]);
    const isSupervisor = useMemo(() => !!user?.isSupervisor, [user]);

    const params = useQuery();
    const [, setSearch] = useSearchParams();

    const tabs = useMemo(
        () =>
            [
                {
                    key: 'access-and-customisation',
                    name: 'User Access & Customisation',
                    Content: UserAccessCustomisation,
                    show: isGlobalAdmin,
                },
                {
                    key: 'supervision',
                    name: 'User Supervision',
                    Content: AdminUserSupervision,
                    show: isEitherAdmin,
                },
                {
                    key: 'audit-log',
                    name: 'Audit Log',
                    Content: AdminAuditLog,
                    show: isGlobalAdmin,
                },
            ].filter((tab) => tab.show),
        [isEitherAdmin, isGlobalAdmin],
    );
    const [currentTab, setCurrentTab] = useState<number>(0);
    useLayoutEffect(() => {
        setCurrentTab(params['tab'] ? tabs.findIndex((t) => t.key === params['tab']) ?? 0 : 0);
    }, [tabs]);

    const changeTab = (index: number) => {
        setCurrentTab(index);
        setSearch({ ...params, tab: tabs[index].key });
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error || !currentAssignment) {
        return <SimpleAlert variant="subtle">Could not load User Permissions</SimpleAlert>;
    }

    if (!isEitherAdmin && !isSupervisor) {
        return (
            <Flex justifyContent="center" mt={6}>
                <SimpleAlert variant="top-accent" title="Not Authorised">
                    Not an administrator.
                </SimpleAlert>
            </Flex>
        );
    }

    return (
        <Box maxH={'auto'} w="full" p={6}>
            <Box bg="offWhite" rounded={'none'} w="full">
                <VStack align="flex-start" spacing={6} w="full">
                    <FocusHeader size={'md'} textColor={'blue.500'} pt={6} px={6}>
                        Administrator
                    </FocusHeader>
                    <Tabs variant={'enclosed'} isLazy index={currentTab} onChange={changeTab} w="full">
                        <TabList px={6} gridGap={6}>
                            {tabs.map((tab, i) => (
                                <Tab
                                    key={i}
                                    tabIndex={i}
                                    border={'1px'}
                                    borderColor={'gray.200'}
                                    fontWeight={i === currentTab ? 'semibold' : 'normal'}
                                >
                                    {tab.name}
                                </Tab>
                            ))}
                        </TabList>
                        <TabPanels>
                            {tabs.map((tab, i) => (
                                <TabPanel key={i} borderColor={'gray.100'} p={0}>
                                    <tab.Content assignment={currentAssignment} />
                                </TabPanel>
                            ))}
                        </TabPanels>
                    </Tabs>
                </VStack>
            </Box>
        </Box>
    );
};

export default Admin;
